import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CLINIC, HOURS, WEEK, CLOSED_DAYS, CONDITION_GROUPS, DOCTOR, telLink, waLink,
} from "./content";
import { useClinicStatus, istNow } from "./useSite";

/** Responsive photo: WebP with JPEG fallback. */
export function Photo({ name, alt, width, height, className = "", eager = false, sizes }) {
  return (
    <picture className={className}>
      <source srcSet={`/site/${name}.webp`} type="image/webp" />
      <img
        src={`/site/${name}.jpg`}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        sizes={sizes}
        {...(eager ? { fetchpriority: "high" } : {})}
      />
    </picture>
  );
}

/** Footprint mark used in the logo. */
export function FootMark({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <ellipse cx="9" cy="13.5" rx="3.4" ry="5.6" transform="rotate(-12 9 13.5)" fill="currentColor" />
      <ellipse cx="9.8" cy="20.6" rx="2.5" ry="2.1" fill="currentColor" />
      <ellipse cx="16.4" cy="6.6" rx="2.9" ry="4.6" transform="rotate(14 16.4 6.6)" fill="currentColor" opacity="0.55" />
      <ellipse cx="15.2" cy="12.4" rx="2.1" ry="1.8" fill="currentColor" opacity="0.55" />
    </svg>
  );
}

export function Logo({ light = false }) {
  return (
    <Link to="/" className={`s-logo ${light ? "s-logo--light" : ""}`} aria-label={`${CLINIC.name}, home`}>
      <span className="s-logo__mark"><FootMark /></span>
      <span className="s-logo__text">
        <span className="s-logo__name lh-1">Sai Heal n Walk</span>
        <span className="s-logo__sub lh-1 mt-0">Physiotherapy Clinic, Khopoli</span>
      </span>
    </Link>
  );
}

/** "Open now · until 2:00 PM" pill. */
export function StatusPill({ className = "" }) {
  const st = useClinicStatus();
  return (
    <span className={`s-status ${st.open ? "is-open" : "is-closed"} ${className}`}>
      <span className="s-status__dot" aria-hidden="true" />
      <strong>{st.text}</strong>
      {st.detail ? <span>{st.detail}</span> : null}
    </span>
  );
}

/** Tabbed list of conditions treated (English + Marathi). */
export function ConditionExplorer({ compact = false }) {
  const [active, setActive] = useState(CONDITION_GROUPS[0].key);
  const group = CONDITION_GROUPS.find((g) => g.key === active);
  return (
    <div className={`s-cond ${compact ? "s-cond--compact" : ""}`}>
      <div className="s-cond__tabs" role="tablist" aria-label="Condition groups">
        {CONDITION_GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            role="tab"
            id={`tab-${g.key}`}
            aria-selected={g.key === active}
            aria-controls={`panel-${g.key}`}
            className={`s-cond__tab ${g.key === active ? "is-active" : ""}`}
            onClick={() => setActive(g.key)}
          >
            <i className={`bi ${g.icon}`} aria-hidden="true" />
            {g.label}
            <span className="s-cond__count">{g.items.length}</span>
          </button>
        ))}
      </div>
      <ul className="s-cond__list" role="tabpanel" id={`panel-${group.key}`} aria-labelledby={`tab-${group.key}`}>
        {group.items.map((it) => (
          <li key={it.en}>
            <span className="s-cond__en">{it.en}</span>
            {it.mr ? <span className="s-cond__mr" lang="mr">{it.mr}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Opening hours (Mon-Sat share the same hours), today highlighted. */
export function HoursTable() {
  const { day } = istNow();
  const sundayToday = CLOSED_DAYS.includes(day);
  return (
    <table className="s-hours">
      <caption className="visually-hidden">Clinic opening hours</caption>
      <tbody>
        <tr className={!sundayToday ? "is-today" : ""}>
          <th scope="row">
            Monday – Saturday
            {!sundayToday ? <span className="s-hours__today">Today: {WEEK[day]}</span> : null}
          </th>
          <td>{HOURS.map((h) => <span key={h.label} className="s-hours__slot"><span className="s-muted">{h.label}</span> {h.text}</span>)}</td>
        </tr>
        <tr className={sundayToday ? "is-today" : ""}>
          <th scope="row">Sunday{sundayToday ? <span className="s-hours__today">Today</span> : null}</th>
          <td><span className="s-hours__closed">Closed</span></td>
        </tr>
      </tbody>
    </table>
  );
}

/** Map + address + hours + actions. */
export function VisitBlock() {
  return (
    <div className="s-visit">
      <div className="s-visit__map">
        <iframe
          title="Map showing Sai Heal n Walk Physiotherapy Clinic, Khopoli"
          src={CLINIC.mapEmbed}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
      <div className="s-visit__info">
        <div className="s-visit__row">
          <i className="bi bi-geo-alt" aria-hidden="true" />
          <div>
            <div className="s-visit__label">Address</div>
            <address className="s-visit__value">
              {CLINIC.name}<br />
              {CLINIC.address[0]}<br />
              {CLINIC.address[1]}
            </address>
            <div className="s-visit__hint">Landmark: {CLINIC.landmark}</div>
          </div>
        </div>
        <div className="s-visit__row">
          <i className="bi bi-clock" aria-hidden="true" />
          <div className="w-100">
            <div className="s-visit__label d-flex align-items-center justify-content-between gap-2 flex-wrap">
              Opening hours <StatusPill />
            </div>
            <HoursTable />
          </div>
        </div>
        <div className="s-visit__actions">
          <a className="s-btn s-btn--primary" href={CLINIC.directionsUrl} target="_blank" rel="noreferrer">
            <i className="bi bi-signpost-split" aria-hidden="true" /> Get directions
          </a>
          <a className="s-btn s-btn--ghost" href={telLink}>
            <i className="bi bi-telephone" aria-hidden="true" /> Call
          </a>
          <a className="s-btn s-btn--wa" href={waLink()} target="_blank" rel="noreferrer">
            <i className="bi bi-whatsapp" aria-hidden="true" /> WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

/** Doctor profile with education. */
export function DoctorBlock({ full = false }) {
  return (
    <div className="s-doc">
      <div className="s-doc__photo">
        <Photo name="doctor-portrait" alt="Dr. Yuganta Mhatre Angre at her desk in the clinic" width={780} height={917} />
        <div className="s-doc__badge">
          <strong>BPT, MPT</strong>
          <span>Physiotherapist</span>
        </div>
      </div>
      <div className="s-doc__body">
        <p className="s-kicker">Meet your physiotherapist</p>
        <h2 className="s-h2">{CLINIC.doctor}</h2>
        <p className="s-lead">{DOCTOR.intro}</p>
        <ol className="s-edu">
          {DOCTOR.education.map((e) => (
            <li key={e.degree}>
              <strong>{e.degree}</strong>
              <span>{e.place}</span>
            </li>
          ))}
        </ol>
        <p>{DOCTOR.background}</p>
        {full ? DOCTOR.approach.map((p) => <p key={p.slice(0, 20)}>{p}</p>) : (
          <p>{DOCTOR.approach[0]}</p>
        )}
        <div className="s-doc__actions">
          <Link to="/book" className="s-btn s-btn--primary">
            <i className="bi bi-calendar2-check" aria-hidden="true" /> Book with Dr. Yuganta
          </Link>
          {!full ? (
            <Link to="/about" className="s-btn s-btn--ghost">About the clinic</Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Closing call-to-action band used at the bottom of pages. */
export function BookBand({ title = "Ready to move without pain?", text = "Book a visit online in under a minute, or call and WhatsApp us. Prior appointment is preferred." }) {
  return (
    <section className="s-band" aria-labelledby="band-title">
      <div className="container s-band__inner">
        <div>
          <h2 id="band-title" className="s-band__title">{title}</h2>
          <p className="s-band__text">{text}</p>
        </div>
        <div className="s-band__actions">
          <Link to="/book" className="s-btn s-btn--light">
            <i className="bi bi-calendar2-check" aria-hidden="true" /> Book appointment
          </Link>
          <a href={waLink()} target="_blank" rel="noreferrer" className="s-btn s-btn--outline-light">
            <i className="bi bi-whatsapp" aria-hidden="true" /> WhatsApp
          </a>
          <a href={telLink} className="s-btn s-btn--outline-light">
            <i className="bi bi-telephone" aria-hidden="true" /> {CLINIC.phoneDisplay}
          </a>
        </div>
      </div>
    </section>
  );
}
